<!--
  An inscription with no annotatable image, or one the annotator navigated to directly.

  Its job is to be honest about *why* there is nothing to box: 0 images, an orphan/ambiguous
  image link, or an image indexed from the offline `www/` tree rather than from a bucket. Those
  are catalog findings, not bugs, and the queue's block reasons are shown verbatim.
-->
<script lang="ts">
  import { api } from '../lib/api/client';
  import { asProblem, type ApiProblem } from '../lib/api/problem';
  import type { InscriptionDetail } from '../lib/api/types';
  import { tokenLabel } from '../lib/draft';
  import { glyphOrientation, orientationNote } from '../lib/glyphs';
  import { statusLabel } from '../lib/imageStatus';
  import { href } from '../lib/router.svelte';
  import { glyphPref } from '../lib/stores/glyphPref.svelte';
  import ListenControl from './ListenControl.svelte';
  import GlyphImage from './GlyphImage.svelte';

  interface Props {
    inscriptionId: number;
  }

  const { inscriptionId }: Props = $props();

  let detail = $state<InscriptionDetail | null>(null);
  let problem = $state<ApiProblem | null>(null);

  $effect(() => {
    let cancelled = false;
    detail = null;
    problem = null;
    api
      .inscriptionById(inscriptionId)
      .then((value) => {
        if (!cancelled) detail = value;
      })
      .catch((error: unknown) => {
        if (!cancelled) problem = asProblem(error);
      });
    return () => {
      cancelled = true;
    };
  });
</script>

<div class="view col">
  {#if problem}
    <div class="banner error">
      <strong>{problem.code}</strong>
      <p class="small">{problem.message}</p>
      <a href={href({ name: 'queue', task: null })}>Back to the work queue</a>
    </div>
  {:else if !detail}
    <p class="muted">Loading inscription {inscriptionId}…</p>
  {:else}
    <div class="panel col">
      <h1>{detail.siglum_raw}</h1>
      <div class="small muted mono">
        id {detail.id} · key {detail.siglum} · {detail.ociana_id ?? 'no OCIANA id'}
      </div>
      {#if detail.translit}
        <div class="glyph translit">{detail.translit}</div>
        {#if detail.tokens?.length}
          <ListenControl tokens={detail.tokens} siglum={detail.siglum} />
        {/if}
      {:else}
        <p class="banner warn small">
          This record has no transliteration, so there is no expected-letter sequence and it is
          excluded from the annotation queue.
        </p>
      {/if}
      {#if detail.translation}
        <p class="small muted">{detail.translation}</p>
      {/if}
      {#if detail.tokens.length}
        <div class="row wrap chips glyph">
          {#each detail.tokens as token (token.index)}
            <span
              class="chip"
              class:uncertain={token.uncertain}
              class:restored={token.restored}
              class:with-glyph={glyphPref.show}
            >
              {tokenLabel(token)}
              {#if glyphPref.show}
                <GlyphImage
                  letterClass={token.letter_class}
                  mirrored={glyphOrientation(detail.reading_direction).mirrored}
                  turned={glyphOrientation(detail.reading_direction).turned}
                  height={26}
                  label={token.display}
                />
              {/if}
            </span>
          {/each}
        </div>
        {#if glyphPref.show}
          <p class="small muted forms-note">
            {orientationNote(detail.reading_direction)} — OCIANA's font renderings, a reference for
            reading the transliteration rather than a picture of this stone.
          </p>
        {/if}
      {/if}
    </div>

    <div class="panel col">
      <h2>Images</h2>
      {#if detail.images.length === 0}
        <p class="small">
          No imagery is linked to this record, so it cannot be annotated. That is a catalog
          finding, not an error: the inscription↔image link is a key-prefix join and some
          records simply have no matching objects.
        </p>
      {:else}
        <table class="small">
          <thead>
            <tr>
              <th>id</th><th>key</th><th>source</th><th>link</th><th>status</th>
              <th>shares a rock face with</th><th></th>
            </tr>
          </thead>
          <tbody>
            {#each detail.images as image (image.id)}
              <tr>
                <td class="mono">{image.id}</td>
                <td class="mono">{image.storage_key}</td>
                <td>{image.image_source}</td>
                <td>{image.link}</td>
                <td>{statusLabel(image.status)}</td>
                <!--
                  The column that explains a six-image record with three usable images. OCIANA
                  files one panel photograph under every inscription catalogued on the rock
                  face, so these rows are correctly linked and still may not show this text.
                -->
                <td>
                  {#if image.shared_with_sigla.length}
                    <span title={image.shared_with_sigla.join(', ')}>
                      {image.shared_with_sigla.length} other{image.shared_with_sigla.length === 1
                        ? ''
                        : 's'}
                    </span>
                  {:else}
                    <span class="muted">—</span>
                  {/if}
                </td>
                <td>
                  <a href={href({ name: 'image', imageId: image.id })}>Annotate</a>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
        {#if detail.images.some((image) => image.shared_with_sigla.length > 0)}
          <p class="small muted">
            A photograph listed as sharing a rock face is filed under other inscriptions too,
            byte for byte. It very likely contains texts other than this one, and may not
            contain this one at all — that is OCIANA's cataloguing, not a broken link. An
            annotator who opens one and finds no trace of this record should mark it
            <em>not this inscription</em> rather than skipping it.
          </p>
        {/if}
      {/if}
      {#if detail.queue_block_reasons.length}
        <div class="banner warn small">
          <strong>Blocked from the work queue</strong>
          <ul>
            {#each detail.queue_block_reasons as reason (reason)}
              <li>{reason}</li>
            {/each}
          </ul>
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .view {
    padding: 12px;
    gap: 12px;
    max-width: 1000px;
    margin: 0 auto;
    width: 100%;
  }

  .translit {
    font-size: 1.4rem;
    word-break: break-word;
  }

  .chips {
    gap: 4px;
  }

  .chip {
    border: 2px solid var(--line);
    border-radius: 5px;
    padding: 1px 6px;
  }

  /* Transliteration over reference form, the same pairing the box editor's palette shows. */
  .chip.with-glyph {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    padding: 2px 6px 3px;
    line-height: 1.2;
  }

  .forms-note {
    margin: 0;
  }

  .chip.uncertain {
    border-style: dashed;
    border-color: var(--uncertain);
  }

  .chip.restored {
    border-style: dotted;
    border-color: var(--restored);
  }

  ul {
    margin: 0;
    padding-left: 18px;
  }
</style>
